import { useCallback, useEffect, useMemo, useRef } from 'react'
import { useUploadWorkflowState, StagingWorkflowStep } from './useUploadWorkflowState'
import { useAuth } from '@/contexts/AuthContext'
import { FirestoreService } from '@/services/firestoreService'
import { UploadProcessingService } from '@/services/uploadProcessingService'
import { StagingEarningsProcessingService } from '@/services/stagingEarningsProcessingService'
import { ColumnMappingService } from '@/services/columnMappingService'
import { DataExtractionService } from '@/services/dataExtractionService'
import { DriverMasterService } from '@/services/driverMasterService'
import { WorkflowValidator } from '@/services/workflowValidator'
import {
  UploadStaging,
  Upload,
  Platform,
  DriverMatchResult,
  SimpleProcessingProgress
} from '@/types'

/**
 * Controller hook that encapsulates all business logic for the upload workflow.
 * The page component calls intent-level methods instead of touching services or raw state.
 */

export interface UploadWorkflowController {
  state: ReturnType<typeof useUploadWorkflowState>['state']
  updateState(updates: Partial<ReturnType<typeof useUploadWorkflowState>['state']>): void
  setStep(step: StagingWorkflowStep): void
  saveConfiguration(platform: string, period: string): Promise<void>
  runFieldMapping(): Promise<void>
  ensureDriverMatches(): Promise<void>
  processDriverActions(): Promise<void>
  ignoreRow(rowIndex: number): void
  unignoreRow(rowIndex: number): void
  canProceedToNextStep(): boolean
  resetState(): void
}

export function useUploadWorkflowController(uploadId?: string): UploadWorkflowController {
  const { currentUser } = useAuth()
  const workflow = useUploadWorkflowState()

  const {
    state,
    setStep: setWorkflowStep,
    setLoading: setWorkflowLoading,
    setProcessingProgress: setWorkflowProcessingProgress,
    updateState: updateWorkflowState,
    resetState
  } = workflow

  // Keep a mutable reference to state so async callbacks always read the latest values.
  const stateRef = useRef(state)
  const initializingUploadsRef = useRef(new Set<string>())
  const initializedUploadsRef = useRef(new Set<string>())
  useEffect(() => {
    stateRef.current = state
  }, [state])

  const updateProcessingProgress = useCallback((progress: SimpleProcessingProgress | null) => {
    setWorkflowProcessingProgress(progress)
  }, [setWorkflowProcessingProgress])

  const ensureDriverMatches = useCallback(async () => {
    if (!currentUser) return
    const currentState = stateRef.current
    if (!currentState.uploadStaging) return
    if (!currentState.uploadStaging.normalizedData?.length) return

    setWorkflowLoading(true)

    try {
      const uploadProcessingService = new UploadProcessingService(currentUser)

      // Run the server-side matching job while streaming progress into simplified UI updates.
      await uploadProcessingService.processUploadStaging(
        currentState.uploadStaging.id,
        (progress) => {
          updateProcessingProgress({
            current: progress.processedRows,
            total: progress.totalRows,
            percentage: progress.percentage,
            status: progress.currentStatus,
            estimatedTimeRemaining: progress.estimatedTimeRemaining
          })
        }
      )

      const firestoreService = new FirestoreService(currentUser)
      const updatedStaging = await firestoreService.getUploadStaging(currentState.uploadStaging.id)

      if (updatedStaging) {
        const driverMatches = updatedStaging.driverMatches || []

        updateWorkflowState({
          uploadStaging: updatedStaging,
          driverMatches,
          unmatchedActions: getDefaultUnmatchedActions(driverMatches),
          stagingLoading: false,
          processingProgress: null
        })
      } else {
        updateWorkflowState({
          stagingLoading: false,
          processingProgress: null
        })
      }
    } catch (error) {
      console.error('[UploadWorkflow] Driver matching failed:', error)
      updateWorkflowState({
        stagingLoading: false,
        processingProgress: null
      })
      alert(`Driver matching failed: ${error instanceof Error ? error.message : 'Unknown error'}`)
    }
  }, [currentUser, setWorkflowLoading, updateProcessingProgress, updateWorkflowState])

  const initialize = useCallback(async (id: string) => {
    if (!currentUser) return

    const initializingUploads = initializingUploadsRef.current
    const initializedUploads = initializedUploadsRef.current

    if (initializedUploads.has(id) || initializingUploads.has(id)) {
      return
    }

    initializingUploads.add(id)
    setWorkflowLoading(true)

    try {
      const firestoreService = new FirestoreService(currentUser)

      // Load persisted staging or create a brand-new shell if this is the first visit.
      const stagingResult = await getOrCreateStaging(id, firestoreService)
      if (!stagingResult) {
        console.error('[UploadWorkflow] Failed to create or load staging')
        updateWorkflowState({ stagingLoading: false })
        return
      }

      const { staging, isNew } = stagingResult

      const upload = await firestoreService.getUpload(staging.uploadId)
      if (!upload) {
        console.error('[UploadWorkflow] No original upload found for staging')
        updateWorkflowState({ stagingLoading: false })
        return
      }

      const drivers = await DriverMasterService.getDrivers(currentUser)
      const { identifierMapping, fieldMapping, shouldSkip } = getInitialMappings(staging, upload)
      const persistedPeriod = staging.period || upload.period || ''

      updateWorkflowState({
        uploadStaging: staging,
        originalUpload: upload,
        selectedPlatform: staging.platform || upload.platform,
        selectedPeriod: isNew ? '' : persistedPeriod,
        driverMatches: staging.driverMatches || [],
        availableDrivers: drivers,
        identifierMapping,
        fieldMapping,
        shouldSkipFieldMapping: shouldSkip,
        unmatchedActions: getDefaultUnmatchedActions(staging.driverMatches || []),
        stagingLoading: false,
        processingProgress: null,
        skippedSteps: {}
      })
      initializedUploads.add(id)

      const needsMatching = staging.normalizedData?.length &&
        (!staging.driverMatches || staging.driverMatches.length === 0)

      if (needsMatching) {
        // Kick matching immediately so driver review is ready when the user arrives.
        await ensureDriverMatches()
      }
    } catch (error) {
      console.error('[UploadWorkflow] Failed to initialize workflow:', error)
      updateWorkflowState({ stagingLoading: false })
    } finally {
      initializingUploads.delete(id)
    }
  }, [currentUser, ensureDriverMatches, setWorkflowLoading, updateWorkflowState])

  const saveConfiguration = useCallback(async (platform: string, period: string) => {
    if (!currentUser) return
    const currentState = stateRef.current
    if (!currentState.uploadStaging || !currentState.originalUpload) return

    try {
      const firestoreService = new FirestoreService(currentUser)

      await firestoreService.updateUpload(currentState.originalUpload.id, {
        platform,
        period
      })

      await firestoreService.updateUploadStaging(currentState.uploadStaging.id, {
        platform: platform as Platform,
        period
      })

      updateWorkflowState({
        selectedPlatform: platform,
        selectedPeriod: period,
        originalUpload: { ...currentState.originalUpload, platform, period },
        uploadStaging: { ...currentState.uploadStaging, platform: platform as Platform, period }
      })
    } catch (error) {
      console.error('[UploadWorkflow] Failed to save configuration:', error)
      alert(`Failed to save configuration: ${error instanceof Error ? error.message : 'Unknown error'}`)
    }
  }, [currentUser, updateWorkflowState])

  const runFieldMapping = useCallback(async () => {
    if (!currentUser) return
    const currentState = stateRef.current
    if (!currentState.uploadStaging) return

    try {
      const firestoreService = new FirestoreService(currentUser)
      const upload = currentState.originalUpload ||
        await firestoreService.getUpload(currentState.uploadStaging.uploadId)

      if (!upload) {
        console.error('[UploadWorkflow] Unable to load upload for field mapping')
        return
      }

      // Validate and prepare column mappings
      const {
        columnMapping,
        missingRequiredFields,
        unmappedHeaders,
        duplicateHeaders
      } = ColumnMappingService.validateAndPrepareMappings(
        currentState.identifierMapping,
        currentState.fieldMapping,
        upload.headers,
        currentState.uploadStaging.platform
      )

      if (missingRequiredFields.length > 0) {
        alert(`Obligatoriska fält saknas: ${missingRequiredFields.join(', ')}`)
        return
      }

      if (duplicateHeaders.length > 0) {
        alert(`Samma kolumn har valts flera gånger: ${duplicateHeaders.join(', ')}`)
        return
      }

      // Save column mappings
      await firestoreService.updateUploadStaging(currentState.uploadStaging.id, {
        columnMapping,
        updatedAt: new Date()
      })

      // Extract data using validated mappings
      const extractionResult = DataExtractionService.extractData(
        upload.data,
        upload.headers,
        currentState.uploadStaging.platformInfo,
        columnMapping,
        currentState.uploadStaging.platform
      )

      // Save extracted data
      await firestoreService.updateUploadStaging(currentState.uploadStaging.id, {
        normalizedData: extractionResult.normalizedData,
        updatedAt: new Date()
      })

      // Update local state
      const nextSkippedSteps = { ...(currentState.skippedSteps || {}) }
      if (nextSkippedSteps['field-mapping']) {
        delete nextSkippedSteps['field-mapping']
      }

      updateWorkflowState({
        identifierMapping: columnMapping.identifierFields,
        fieldMapping: columnMapping.earningsFields,
        shouldSkipFieldMapping: false,
        skippedSteps: nextSkippedSteps,
        uploadStaging: {
          ...currentState.uploadStaging,
          columnMapping,
          normalizedData: extractionResult.normalizedData
        }
      })

      if (unmappedHeaders.length > 0) {
        console.info('[UploadWorkflow] Unmapped headers remaining:', unmappedHeaders)
      }
    } catch (error) {
      console.error('[UploadWorkflow] Failed to update column mappings:', error)
      alert(`Failed to update column mappings: ${error instanceof Error ? error.message : 'Unknown error'}`)
    }
  }, [currentUser, updateWorkflowState])

  const createEarnings = useCallback(async () => {
    if (!currentUser) return
    const currentState = stateRef.current
    if (!currentState.uploadStaging) return

    updateWorkflowState({
      isCreatingEarnings: true,
      processingProgress: null
    })

    try {
      const processingService = new StagingEarningsProcessingService(currentUser)
      const earningsResults = await processingService.createEarningsFromStaging(
        currentState.uploadStaging.id,
        currentState.ignoredRows,
        (progress) => {
          updateProcessingProgress({
            current: progress.processedRows,
            total: progress.totalRows,
            percentage: progress.percentage,
            status: progress.currentStatus,
            estimatedTimeRemaining: progress.estimatedTimeRemaining
          })
        }
      )

      updateWorkflowState({
        earningsResults,
        isCreatingEarnings: false,
        processingProgress: null,
        step: 'complete'
      })
    } catch (error) {
      console.error('[UploadWorkflow] Failed to create earnings:', error)
      updateWorkflowState({
        isCreatingEarnings: false,
        processingProgress: null
      })
      alert(`Earnings creation failed: ${error instanceof Error ? error.message : 'Unknown error'}`)
    }
  }, [currentUser, updateProcessingProgress, updateWorkflowState])

  const processDriverActions = useCallback(async () => {
    if (!currentUser) return
    const currentState = stateRef.current
    if (!currentState.uploadStaging) return

    updateWorkflowState({
      isProcessingDriverActions: true,
      processingProgress: null
    })

    try {
      const processingService = new StagingEarningsProcessingService(currentUser)
      const driverActionResults = await processingService.processDriverActions(
        currentState.uploadStaging.id,
        {
          unmatchedActions: currentState.unmatchedActions,
          perfectMatchOverrides: currentState.perfectMatchOverrides,
          ignoredRows: currentState.ignoredRows
        },
        (progress) => {
          updateProcessingProgress({
            current: progress.processedRows,
            total: progress.totalRows,
            percentage: progress.percentage,
            status: progress.currentStatus,
            estimatedTimeRemaining: progress.estimatedTimeRemaining
          })
        }
      )

      updateWorkflowState({
        driverActionResults,
        isProcessingDriverActions: false,
        processingProgress: null,
        step: 'earnings-creation'
      })

      // Immediately move into earnings creation once driver actions are persisted.
      await createEarnings()
    } catch (error) {
      console.error('[UploadWorkflow] Error processing driver actions:', error)
      updateWorkflowState({
        isProcessingDriverActions: false,
        processingProgress: null
      })
      alert(`Driver actions processing failed: ${error instanceof Error ? error.message : 'Unknown error'}`)
    }
  }, [currentUser, createEarnings, updateProcessingProgress, updateWorkflowState])

  const ignoreRow = useCallback((rowIndex: number) => {
    const currentState = stateRef.current
    const ignored = new Set(currentState.ignoredRows)
    ignored.add(rowIndex)

    const unmatched = new Map(currentState.unmatchedActions)
    unmatched.delete(rowIndex)

    const overrides = new Map(currentState.perfectMatchOverrides)
    overrides.delete(rowIndex)

    updateWorkflowState({
      ignoredRows: ignored,
      unmatchedActions: unmatched,
      perfectMatchOverrides: overrides
    })
  }, [updateWorkflowState])

  const unignoreRow = useCallback((rowIndex: number) => {
    const currentState = stateRef.current
    const ignored = new Set(currentState.ignoredRows)
    ignored.delete(rowIndex)
    updateWorkflowState({ ignoredRows: ignored })
  }, [updateWorkflowState])

  const canProceedToNextStep = useCallback((): boolean => {
    return WorkflowValidator.canProceedToNextStep(state)
  }, [state])

  useEffect(() => {
    if (uploadId && currentUser) {
      initialize(uploadId)
    }
  }, [uploadId, currentUser, initialize])

  const resetStateWithCleanup = useCallback(() => {
    initializingUploadsRef.current.clear()
    initializedUploadsRef.current.clear()
    resetState()
  }, [resetState])

  return useMemo(() => ({
    state,
    updateState: updateWorkflowState,
    setStep: setWorkflowStep,
    saveConfiguration,
    runFieldMapping,
    ensureDriverMatches,
    processDriverActions,
    ignoreRow,
    unignoreRow,
    canProceedToNextStep,
    resetState: resetStateWithCleanup
  }), [
    state,
    updateWorkflowState,
    setWorkflowStep,
    saveConfiguration,
    runFieldMapping,
    ensureDriverMatches,
    processDriverActions,
    ignoreRow,
    unignoreRow,
    canProceedToNextStep,
    resetStateWithCleanup
  ])
}

async function getOrCreateStaging(
  uploadId: string,
  firestoreService: FirestoreService
): Promise<{ staging: UploadStaging; isNew: boolean } | null> {
  const existing = await firestoreService.getUploadStagingByUploadId(uploadId)
  if (existing) {
    return { staging: existing, isNew: false }
  }

  const upload = await firestoreService.getUpload(uploadId)
  if (!upload) return null

  const stagingData = DataExtractionService.createUploadStaging(
    uploadId,
    upload.platform as Platform,
    upload.period,
    [],
    {
      detectedPlatform: upload.platform,
      confidence: 1.0,
      allMatches: [],
      shouldAutoSelect: true
    }
  )

  const stagingId = await firestoreService.createUploadStaging(stagingData)
  const created = await firestoreService.getUploadStaging(stagingId)
  if (!created) return null

  return {
    staging: created,
    isNew: true
  }
}

function getInitialMappings(staging: UploadStaging, upload: Upload) {
  if (staging.columnMapping?.identifierFields && staging.columnMapping?.earningsFields) {
    const platformConfig = ColumnMappingService.getConfigForPlatform(upload.platform as Platform)
    const allPlatformFields = [
      ...Object.keys(platformConfig?.identifierFields || {}),
      ...Object.keys(platformConfig?.earningsFields || {})
    ]

    const allMappedFields = [
      ...Object.keys(staging.columnMapping.identifierFields),
      ...Object.keys(staging.columnMapping.earningsFields)
    ]

    const allAvailableFieldsMapped = allPlatformFields.every(field => allMappedFields.includes(field))
    const shouldSkip =
      (staging.columnMapping.missingRequiredFields?.length ?? 1) === 0 &&
      allAvailableFieldsMapped &&
      (staging.columnMapping.confidence ?? 0) >= 95

    return {
      identifierMapping: staging.columnMapping.identifierFields,
      fieldMapping: staging.columnMapping.earningsFields,
      shouldSkip
    }
  }

  return {
    identifierMapping: {},
    fieldMapping: {},
    shouldSkip: false
  }
}

function getDefaultUnmatchedActions(driverMatches: DriverMatchResult[]) {
  const actions = new Map<number, any>()
  driverMatches
    .filter(match => !match.hasMatch)
    .forEach(match => {
      const newDriverName =
        match.extractedName ||
        match.identifiers?.name ||
        `Förare ${match.rowIndex + 1}`

      actions.set(match.rowIndex, {
        rowIndex: match.rowIndex,
        action: 'create_new',
        newDriverName
      })
    })

  return actions
}
